import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import useAuthStore from '../../context/authStore';
import { BRAND } from '../../config/branding';

const navigation = [
  { name: 'Home', href: '/' },
  { 
    name: 'Services', 
    href: '/services',
    submenu: [
      { name: 'Consulting', href: '/services/consulting' },
      { name: 'Training Services (LMS)', href: '/services/training' },
      { name: 'Staffing Services', href: '/services/staffing', submenu: [
          { name: 'For Organizations', href: '/services/staffing' },
          { name: 'For Job Seekers', href: '/services/staffing/job-seekers' },
        ]
      },
    ]
  },
  { name: 'About', href: '/about' },
  { name: 'Contact', href: '/contact' },
];

export default function PublicLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [staffingNestedOpen, setStaffingNestedOpen] = useState(false);
  const [staffingMobileOpen, setStaffingMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const authRef = useRef(null);
  const servicesRef = useRef(null);
  const mobileServicesRef = useRef(null);
  const staffingRef = useRef(null);
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const { isAuthenticated, user, token, isLoading, fetchUser, logout } = useAuthStore();
  const adminPortalUrl = import.meta.env.VITE_ADMIN_URL || '';
  const clientPortalUrl = import.meta.env.VITE_CLIENT_PORTAL_URL || '';

  const buildPortalHref = (baseUrl, pathname, fallback) => {
    if (!baseUrl) return fallback;
    try {
      return new URL(pathname, baseUrl).toString();
    } catch (_err) {
      return fallback;
    }
  };

  const clientLoginHref = buildPortalHref(clientPortalUrl, '/login', '/login');

  const openPortalPath = (baseUrl, pathname, fallbackInternalPath) => {
    const target = buildPortalHref(baseUrl, pathname, '');
    if (target) {
      if (typeof window !== 'undefined') window.location.assign(target);
      return;
    }
    if (fallbackInternalPath) navigate(fallbackInternalPath);
  };

  const openStaffingNested = () => setStaffingNestedOpen(true);

  // If a token exists but auth state is not yet hydrated, fetch the current user
  useEffect(() => {
    const t = localStorage.getItem('token') || token;
    if (t && !isAuthenticated && !isLoading) {
      fetchUser();
    }
  }, [token, isAuthenticated, isLoading, fetchUser]);

  // Close auth menu when clicking outside
  useEffect(() => {
    const onClick = (e) => {
      if (authRef.current && !authRef.current.contains(e.target)) {
        setAuthMenuOpen(false);
      }
      if (
        servicesRef.current &&
        !servicesRef.current.contains(e.target) &&
        (!mobileServicesRef.current || !mobileServicesRef.current.contains(e.target))
      ) {
        setServicesDropdownOpen(false);
        setMobileServicesOpen(false);
      }
      if (staffingRef.current && !staffingRef.current.contains(e.target)) {
        setStaffingNestedOpen(false);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  // Close dropdowns on route change
  useEffect(() => {
    setAuthMenuOpen(false);
    setServicesDropdownOpen(false);
    setMobileServicesOpen(false);
    setStaffingNestedOpen(false);
    setStaffingMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setAuthMenuOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <nav className="w-full px-4 sm:px-6">
          <div className="flex items-center h-24 w-full justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-2">
                {/* Logo image, falls back to placeholder svg if not present */}
                <img src="/stratel-favicon-dark.png" alt="Stratel" className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 object-contain" onError={(e)=>{e.currentTarget.src='/branding/stratel-logo-placeholder.svg'}} />
                <div className="hidden sm:flex flex-col leading-tight">
                  <span className="font-display font-bold text-sm sm:text-xl md:text-3xl text-secondary-900">{BRAND.name}</span>
                  <span className="mt-1 text-xs sm:text-sm text-secondary-500">{BRAND.tagline}</span>
                </div>
              </Link>
            </div>

            {/* Desktop navigation - right-aligned */}
            <div className="hidden lg:flex items-center gap-3 xl:gap-6 ml-auto mr-2">
              {navigation.map((item) => (
                item.submenu ? (
                  <div key={item.name} className="relative" ref={servicesRef}>
                    <div
                      className={`flex items-center text-base xl:text-lg font-medium transition-colors ${
                        location.pathname.startsWith(item.href)
                          ? 'text-primary-600'
                          : 'text-secondary-600 hover:text-secondary-900'
                      }`}
                    >
                      <Link to={item.href} className="px-3 py-2">
                        {item.name}
                      </Link>
                      <button
                        type="button"
                        onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded hover:bg-secondary-100"
                        aria-label={`Open ${item.name} menu`}
                        aria-expanded={servicesDropdownOpen}
                      >
                        <ChevronDown
                          size={18}
                          className={`transition-transform ${servicesDropdownOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                    </div>
                    
                    {servicesDropdownOpen && (
                      <div className="absolute top-full left-0 mt-0 w-56 max-w-[calc(100vw-2rem)] bg-white border border-secondary-200 rounded-lg shadow-lg z-50">
                        {item.submenu.map((subitem) => (
                      subitem.submenu ? (
                        <div key={subitem.name} className="relative" ref={staffingRef} onMouseEnter={openStaffingNested}>
                          <button
                            onClick={() => setStaffingNestedOpen((prev) => !prev)}
                            className={`w-full text-left px-4 py-3 text-base font-medium transition-colors border-b border-secondary-100 last:border-b-0 flex justify-between items-center ${
                              location.pathname.startsWith(subitem.href)
                                ? 'bg-primary-50 text-primary-600'
                                : 'text-secondary-600 hover:bg-secondary-50'
                            }`}
                          >
                            <span>{subitem.name}</span>
                            <ChevronRight className={`w-4 h-4 text-secondary-400 transition-transform ${staffingNestedOpen ? 'rotate-90' : ''}`} />
                          </button>

                          {staffingNestedOpen && (
                            <div
                              className="absolute top-0 left-full w-56 bg-white border border-secondary-200 rounded-lg shadow-lg z-50"
                              onMouseEnter={openStaffingNested}
                            >
                              {subitem.submenu.map((child) => (
                                <Link
                                  key={child.href}
                                  to={child.href}
                                  className={`block px-4 py-3 text-base font-medium transition-colors border-b border-secondary-100 last:border-b-0 ${
                                    location.pathname === child.href
                                      ? 'bg-primary-50 text-primary-600'
                                      : 'text-secondary-600 hover:bg-secondary-50'
                                  }`}
                                >
                                  {child.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <Link
                          key={subitem.href}
                          to={subitem.href}
                          className={`block px-4 py-3 text-base font-medium transition-colors border-b border-secondary-100 last:border-b-0 ${
                            location.pathname === subitem.href
                              ? 'bg-primary-50 text-primary-600'
                              : 'text-secondary-600 hover:bg-secondary-50'
                          }`}
                        >
                          {subitem.name}
                        </Link>
                      )
                    ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`text-base xl:text-lg font-medium transition-colors px-3 py-2 ${
                      location.pathname === item.href
                        ? 'text-primary-600'
                        : 'text-secondary-600 hover:text-secondary-900'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>

            {/* Right-side controls */}
            <div className="hidden lg:flex items-center pr-2">
              {isAuthenticated ? (
                <div className="relative" ref={authRef}>
                  <button
                    onClick={() => setAuthMenuOpen(!authMenuOpen)}
                    className="flex items-center gap-2 btn-primary btn-sm"
                    aria-expanded={authMenuOpen}
                    aria-haspopup="true"
                  >
                    <span className="rounded-full bg-primary-700 text-white w-8 h-8 flex items-center justify-center text-sm font-semibold">
                      {user?.firstName?.[0] || 'U'}
                    </span>
                    <span className="hidden sm:inline">Go to Dashboard</span>
                  </button>

                  {authMenuOpen && (
                    <div className="absolute right-0 mt-2 w-44 bg-white border rounded-md shadow-md z-50">
                      <button
                        onClick={() => {
                          setAuthMenuOpen(false);
                          // smart navigation: go to dashboard if allowed, otherwise to login or admin portal
                          if (!isAuthenticated) {
                            openPortalPath(clientPortalUrl, '/login', '/login');
                            return;
                          }

                          // internal team -> admin portal
                          if (['admin', 'stratel_admin', 'consultant', 'consultant_lead'].includes(user?.role)) {
                            openPortalPath(adminPortalUrl || 'http://localhost:5174', '/', '/login');
                            return;
                          }

                          // otherwise go to client dashboard
                          openPortalPath(clientPortalUrl, '/dashboard', '/dashboard');
                        }}
                        className="block px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100 text-left w-full"
                        title="Go to Dashboard"
                      >
                        Go to Dashboard
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : ((localStorage.getItem('token') || token) && isLoading ? (
                <div className="btn-secondary btn-sm opacity-70 flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary-600"></div>
                  <span className="text-sm">Loading...</span>
                </div>
              ) : (
                <a href={clientLoginHref} className="btn-secondary btn-sm mr-2 text-base xl:text-lg font-medium">
                  Client Login
                </a>
              ))}
            </div> 

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg text-secondary-600 hover:bg-secondary-100"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-secondary-200">
            <div className="px-4 py-4 space-y-3">
              {navigation.map((item) => (
                item.submenu ? (
                  <div key={item.name} ref={mobileServicesRef}>
                    <div
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-base font-medium ${
                        location.pathname.startsWith('/services')
                          ? 'bg-primary-50 text-primary-600'
                          : 'text-secondary-600 hover:bg-secondary-50'
                      }`}
                    >
                      <Link
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex-1 py-1"
                      >
                        {item.name}
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          setMobileServicesOpen((current) => {
                            if (current) setStaffingMobileOpen(false);
                            return !current;
                          });
                        }}
                        className="inline-flex h-10 w-10 items-center justify-center rounded"
                        aria-label={`Open ${item.name} menu`}
                        aria-expanded={mobileServicesOpen}
                      >
                        <ChevronDown
                          size={18}
                          className={`transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                    </div>
                    {mobileServicesOpen && (
                      <div className="bg-secondary-50 rounded mt-2 py-2">
                        {item.submenu.map((subitem) => (
                          subitem.submenu ? (
                            <div key={subitem.name}>
                              <button
                                type="button"
                                onClick={() => setStaffingMobileOpen((current) => !current)}
                                className={`w-full text-left px-4 py-2 text-base font-medium transition-colors ${location.pathname.startsWith(subitem.href) ? 'text-primary-600 bg-white rounded' : 'text-secondary-600'}`}
                              >
                                <span className="flex items-center justify-between w-full">
                                  <span>{subitem.name}</span>
                                  <ChevronDown className={`w-4 h-4 ${staffingMobileOpen ? 'rotate-180' : ''}`} />
                                </span>
                              </button>

                              {staffingMobileOpen && (
                                <div className="bg-secondary-50 rounded mt-2 py-2">
                                  {subitem.submenu.map((child) => (
                                    <Link
                                      key={child.href}
                                      to={child.href}
                                      onClick={() => {
                                        setMobileMenuOpen(false);
                                        setMobileServicesOpen(false);
                                        setStaffingMobileOpen(false);
                                      }}
                                      className={`block px-4 py-2 text-base font-medium transition-colors ${
                                        location.pathname === child.href
                                          ? 'text-primary-600 bg-white rounded'
                                          : 'text-secondary-600'
                                      }`}
                                    >
                                      {child.name}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <Link
                              key={subitem.href}
                              to={subitem.href}
                              onClick={() => {
                                setMobileMenuOpen(false);
                                setMobileServicesOpen(false);
                              }}
                              className={`block px-4 py-2 text-base font-medium transition-colors ${
                                location.pathname === subitem.href
                                  ? 'text-primary-600 bg-white rounded'
                                  : 'text-secondary-600'
                              }`}
                            >
                              {subitem.name}
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-3 py-2 rounded-lg text-base font-medium ${
                      location.pathname === item.href
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-secondary-600 hover:bg-secondary-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              {isAuthenticated ? (
                <>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      // Same logic as desktop: if internal send to admin portal, else go to dashboard
                      if (['admin', 'stratel_admin', 'consultant', 'consultant_lead'].includes(user?.role)) {
                        openPortalPath(adminPortalUrl || 'http://localhost:5174', '/', '/login');
                        return;
                      }
                      openPortalPath(clientPortalUrl, '/dashboard', '/dashboard');
                    }}
                    className="block w-full btn-primary text-center text-base font-medium"
                  >
                    Go to Dashboard
                  </button>
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                      navigate('/');
                    }}
                    className="block w-full mt-2 btn-secondary text-center"
                  >
                    Logout
                  </button>
                </>
              ) : ( (localStorage.getItem('token') || token) && isLoading ? (
                <div className="w-full btn-secondary text-center opacity-70 flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary-600"></div>
                  <span className="">Loading...</span>
                </div>
              ) : (
                <a
                  href={clientLoginHref}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full btn-primary text-center text-base font-medium"
                >
                  Client Login
                </a>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-secondary-900 text-secondary-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company info */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <img src="/branding/stratel-logo-dark.png" alt="Stratel" className="w-14 h-14 md:w-16 md:h-16" onError={(e)=>{e.currentTarget.src='/branding/stratel-logo-placeholder.svg'}} />
                <span className="font-display font-bold text-3xl text-white">
                  Stratel
                </span>
              </div>
              <p className="text-secondary-400 max-w-md">
                Strategic support for long-term care organizations through consulting, training, staffing, and operational solutions.
              </p>
            </div>

            {/* Quick links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-secondary-400 hover:text-white transition-colors"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/privacy" className="text-secondary-400 hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-white font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-secondary-400">
                <li>info@stratelplus.com</li>
                <li>(404) 913-5524 </li>
                <li> 8735 Dunwoody Place Ste R,</li>
                <li>Atlanta, GA 30350</li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-secondary-800 text-center text-secondary-500">
            <p>&copy; {new Date().getFullYear()} Stratel. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
